import React, { useState, useEffect, useContext } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  Table,
  Badge,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Spinner,
} from "reactstrap";

import EmpresaContext from "../../../contexts/Empresa";
import AuthContext from "../../../contexts/Auth";
import api from "../../../services/api";

const VisualizarRotas = ({ show, onClose, notify }) => {
  const { empresaSelecionada } = useContext(EmpresaContext);
  const { auth } = useContext(AuthContext);

  const [rotas, setRotas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rotaSelecionada, setRotaSelecionada] = useState(null);

  useEffect(() => {
    if (show && empresaSelecionada?.id) {
      buscarRotas();
    }
  }, [show, empresaSelecionada]);

  const buscarRotas = async () => {
    setLoading(true);
    try {
      const response = await api.get(
        `/common/empresas/${empresaSelecionada.id}/rotas-visitas`,
        {
          headers: {
            Authorization: `Bearer ${auth.token}`,
          },
        }
      );
      setRotas(response.data.data || []);
    } catch (error) {
      console.error("Erro ao buscar rotas:", error);
      notify("danger", "Erro ao carregar rotas de visitas");
      setRotas([]);
    } finally {
      setLoading(false);
    }
  };

  const formatarData = (data) => {
    return new Date(data).toLocaleDateString('pt-BR');
  };

  const formatarTempo = (minutos) => {
    if (!minutos) return "N/A";
    const horas = Math.floor(minutos / 60);
    const mins = minutos % 60;
    return horas > 0 ? `${horas}h${mins}min` : `${mins}min`;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      planejada: { color: "info", text: "Planejada" },
      em_execucao: { color: "warning", text: "Em Execução" },
      concluida: { color: "success", text: "Concluída" },
      cancelada: { color: "danger", text: "Cancelada" }
    };
    
    const config = statusConfig[status] || { color: "secondary", text: status };
    return <Badge color={config.color}>{config.text}</Badge>;
  };

  const abrirGoogleMaps = (rota) => {
    if (rota.google_maps_url) {
      window.open(rota.google_maps_url, '_blank');
    } else {
      notify("warning", "URL do Google Maps não disponível para esta rota");
    }
  };

  const verDetalhes = (rota) => {
    setRotaSelecionada(rota);
  };

  return (
    <Modal isOpen={show} toggle={onClose} size="xl">
      <ModalHeader toggle={onClose}>
        Rotas de Visitas Registradas
      </ModalHeader>
      <ModalBody>
        {loading ? (
          <div className="text-center py-4">
            <Spinner color="primary" />
            <p className="mt-2">Carregando rotas...</p>
          </div>
        ) : (
          <Row>
            <Col md={rotaSelecionada ? "6" : "12"}>
              {rotas.length === 0 ? (
                <div className="text-center py-4">
                  <p>Nenhuma rota de visita encontrada.</p>
                  <small className="text-muted">
                    Crie sua primeira rota usando o botão "Criar Rota de Visitas"
                  </small>
                </div>
              ) : (
                <Table responsive striped>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Colaborador</th>
                      <th>Data Início</th>
                      <th>Status</th>
                      <th>Tempo Est.</th>
                      <th>Visitas</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rotas.map((rota) => (
                      <tr key={rota.id}>
                        <td>#{rota.id}</td>
                        <td>{rota.colaborador?.name || "N/A"}</td>
                        <td>{formatarData(rota.data_inicio)}</td>
                        <td>{getStatusBadge(rota.status)}</td>
                        <td>{formatarTempo(rota.tempo_estimado_minutos)}</td>
                        <td>{rota.rotaOportunidades?.length || 0}</td>
                        <td>
                          <Button
                            size="sm"
                            color="info"
                            className="mr-1"
                            onClick={() => verDetalhes(rota)}
                          >
                            Ver
                          </Button>
                          {rota.google_maps_url && (
                            <Button
                              size="sm"
                              color="success"
                              onClick={() => abrirGoogleMaps(rota)}
                            >
                              Maps
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Col>
            
            {rotaSelecionada && (
              <Col md="6">
                <Card>
                  <CardHeader>
                    <h6 className="mb-0">
                      Detalhes da Rota #{rotaSelecionada.id}
                      <Button
                        size="sm"
                        color="link"
                        className="float-right p-0"
                        onClick={() => setRotaSelecionada(null)}
                      >
                        ✕
                      </Button>
                    </h6>
                  </CardHeader>
                  <CardBody>
                    <div className="mb-3">
                      <strong>Colaborador:</strong> {rotaSelecionada.colaborador?.name}
                    </div>
                    <div className="mb-3">
                      <strong>Período:</strong> {formatarData(rotaSelecionada.data_inicio)}
                      {rotaSelecionada.data_fim !== rotaSelecionada.data_inicio && 
                        ` até ${formatarData(rotaSelecionada.data_fim)}`}
                    </div>
                    <div className="mb-3">
                      <strong>Status:</strong> {getStatusBadge(rotaSelecionada.status)}
                    </div>
                    <div className="mb-3">
                      <strong>Tempo Estimado:</strong> {formatarTempo(rotaSelecionada.tempo_estimado_minutos)}
                    </div>
                    
                    {rotaSelecionada.endereco_partida && (
                      <div className="mb-3">
                        <strong>Partida:</strong> {rotaSelecionada.endereco_partida}
                      </div>
                    )}
                    
                    {rotaSelecionada.endereco_destino && (
                      <div className="mb-3">
                        <strong>Destino:</strong> {rotaSelecionada.endereco_destino}
                      </div>
                    )}
                    
                    {rotaSelecionada.observacoes && (
                      <div className="mb-3">
                        <strong>Observações:</strong>
                        <p className="text-muted small">{rotaSelecionada.observacoes}</p>
                      </div>
                    )}
                    
                    <div className="mb-3">
                      <strong>Visitas ({rotaSelecionada.rotaOportunidades?.length || 0}):</strong>
                      {rotaSelecionada.rotaOportunidades?.map((item, index) => (
                        <div key={index} className="small text-muted ml-2">
                          {item.ordem}. {item.oportunidade?.cliente?.nome || "Cliente"} - 
                          {item.oportunidade?.nome || "Oportunidade"}
                        </div>
                      ))}
                    </div>
                    
                    {rotaSelecionada.google_maps_url && (
                      <Button
                        color="success"
                        size="sm"
                        block
                        onClick={() => abrirGoogleMaps(rotaSelecionada)}
                      >
                        🗺️ Abrir no Google Maps
                      </Button>
                    )}
                  </CardBody>
                </Card>
              </Col>
            )}
          </Row>
        )}
      </ModalBody>
    </Modal>
  );
};

export default VisualizarRotas;
